import React, { useState } from 'react'
import styled from '@emotion/styled'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { AuthorisedUser, WorkflowActions, EntityTypeEnum } from 'api'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { MenuContainer, ActionItem, ActionItemTemplate } from 'view/components/menus'
import { DropdownListbox, OptionTemplateProps, OptionKey } from 'view/components/options'
import { Avatar } from './Avatar'
import { UserPreferenceDialog } from 'view/shared/userPreferenceDialog'
import { AboutDialog } from './AboutDialog'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { platformDetailSelector } from 'selectors/platformSelectors'
import { ConnectionActions } from 'store/connection'
import { NotificationActions, ShowDialogArgs, NotificationType } from 'store/notification'
import { CorrelatedResponseCallback } from 'store/notification/notificationTypes'
import { Translation, Locale } from 'locale'
import * as mixin from 'view/styles/mixins'

interface UserSettingsProps extends ModalConsumerProps {
  authorisedUser: AuthorisedUser
  onShowUserPreferenceDialog: (
    entityTypeEnum: EntityTypeEnum,
    entityId: string,
    correlatedResponseCallback?: CorrelatedResponseCallback
  ) => unknown
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
}

const mapStateToProps = createStructuredSelector({
  authorisedUser: createAuthorisedUserSelector(),
  platformDetails: platformDetailSelector
})

const mapDispatchToProps = {
  onShowUserPreferenceDialog: WorkflowActions.createEditWorkflow,
  onShowDialog: NotificationActions.showDialog
}

const UserSettingsContainer = styled.div`
  ${mixin.flexCenterContentCrossAxis};
  cursor: pointer;
  outline: 0;
`

const MenuContainerStyled = styled(MenuContainer)`
  display: flex;
  align-items: center;
`

const UserSettingsComponent: React.FunctionComponent<UserSettingsProps> = props => {
  const { authorisedUser, showModal, onShowUserPreferenceDialog, onShowDialog } = props

  const [isDroppedDown, setDroppedDown] = useState<boolean>(false)

  const handleToggleClick = () => setDroppedDown(!isDroppedDown)

  const updateMenuVisible = (visible: boolean) => setDroppedDown(visible)

  const renderActionItem = (optionProps: OptionTemplateProps<any, OptionKey>) => (
    <ActionItemTemplate {...optionProps}>{optionProps.optionText}</ActionItemTemplate>
  )

  const handleOptionClick = (_actionIdx: number, action: ActionItem, _evt: React.MouseEvent) => {
    action.onClick()
  }

  const handleUserPreferences = () => {
    const correlatedResponseCallback = (workflowId: string) => {
      showModal(<UserPreferenceDialog workflowId={workflowId} entityId={authorisedUser.userPreference.id} />)
    }

    onShowUserPreferenceDialog(EntityTypeEnum.USER_PREFERENCE, authorisedUser.userPreference.id, correlatedResponseCallback)
  }

  const showAboutDialog = () => {
    showModal(<AboutDialog />)
  }

  const handleLogout = () => {
    onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_LOGOUT_TITLE),
      messageText: Locale.translate(Translation.SC_LOGOUT_PROMPT),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [ConnectionActions.signOut()]
      }
    })
  }

  const actions: ActionItem[] = [
    {
      label: Locale.translate(Translation.TC_ACTION_USER_PREFERENCES),
      onClick: handleUserPreferences
    },
    {
      label: Locale.translate(Translation.TC_ACTION_ABOUT),
      onClick: showAboutDialog
    },
    {
      label: Locale.translate(Translation.TC_ACTION_LOGOUT),
      onClick: handleLogout
    }
  ]

  const primaryComponent = (
    <UserSettingsContainer onClick={handleToggleClick}>
      <Avatar authorisedUser={authorisedUser} />
    </UserSettingsContainer>
  )

  return (
    <MenuContainerStyled
      dataTestId={'UserSettings-MenuContainerStyled'}
      primaryComponent={primaryComponent}
      menuVisible={isDroppedDown}
      onUpdateMenuVisible={updateMenuVisible}>
      <DropdownListbox
        optionKeys={actions.map((_action, idx) => idx)}
        getOption={(actionIdx: number) => actions[actionIdx]}
        getOptionText={action => action.label}
        onOptionClick={handleOptionClick}
        optionTemplate={renderActionItem}
      />
    </MenuContainerStyled>
  )
}

export const UserSettings = withModal(connect(mapStateToProps, mapDispatchToProps)(UserSettingsComponent))
